import React, { useState, useEffect } from 'react';
import { Loader2, School, RotateCcw, History } from 'lucide-react';
import { validateApi, getInitialQuestion, getNextQuestion } from './api';
import type { QuestionAnswer } from './types';

function App() {
  const [question, setQuestion] = useState<string>('Loading...');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<QuestionAnswer[]>([]);
  const [isFinished, setIsFinished] = useState(false);
  const [finalDecision, setFinalDecision] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    initialize();
  }, []);

  async function initialize() {
    setIsLoading(true);
    setHistory([]);
    setIsFinished(false);
    setFinalDecision(null);
    setShowHistory(false);
    try {
      const isValid = await validateApi();
      if (!isValid) {
        setError('API Key is invalid or not configured. Pumasok ka na lang par antamad mo naman.');
        return;
      }

      const initialQuestion = await getInitialQuestion();
      setQuestion(initialQuestion);
      setError(null);
    } catch (err) {
      setError('Failed to initialize the application. Pumasok ka na lang par antamad mo naman.');
    } finally {
      setIsLoading(false);
    }
  }

  const handleAnswer = async (answer: 'Yes' | 'No' | 'Maybe') => {
    if (isFinished) return;
    
    setIsLoading(true);
    try {
      const currentQA: QuestionAnswer = {
        question,
        answer
      };
      
      const updatedHistory = [...history, currentQA];
      setHistory(updatedHistory);

      const nextResponse = await getNextQuestion(updatedHistory);
      
      if (nextResponse.startsWith('FINAL_DECISION:')) {
        setIsFinished(true);
        setFinalDecision(nextResponse.replace('FINAL_DECISION:', '').trim());
      } else {
        setQuestion(nextResponse);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to get the next question. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const shouldGoToSchool = finalDecision?.toLowerCase().includes('should go') || 
                          finalDecision?.toLowerCase().includes('better go') ||
                          finalDecision?.toLowerCase().includes('need to go') ||
                          finalDecision?.toLowerCase().includes('have to go');

  if (error) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="text-red-600 text-center">
            <h2 className="text-2xl font-bold mb-4">Error</h2>
            <p>{error}</p>
            <button
              onClick={initialize}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <School className="w-12 h-12 text-indigo-600" />
          <div className="flex gap-2">
            {history.length > 0 && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="p-2 text-gray-600 hover:text-indigo-600 transition-colors relative group"
                title="Show History"
              >
                <History className="w-6 h-6" />
                <span className="absolute -top-1 -right-1 bg-indigo-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {history.length}
                </span>
              </button>
            )}
            <button
              onClick={initialize}
              className="p-2 text-gray-600 hover:text-indigo-600 transition-colors"
              title="Start Over"
            >
              <RotateCcw className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        <h1 className="text-3xl font-bold text-center text-gray-900 mb-8">
          Papasok Ako o Di Ako Papasok?
        </h1>

        <div className="bg-indigo-50 rounded-lg p-6 mb-8">
          {isLoading ? (
            <div className="flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
            </div>
          ) : isFinished ? (
            <div className="text-center">
              <h3 className="text-xl font-bold mb-4">Final Decision</h3>
              <div className={`text-2xl font-black mb-4 ${shouldGoToSchool ? 'text-red-600' : 'text-green-600'}`}>
                {shouldGoToSchool ? 'PAPASOK' : 'DI PAPASOK'}
              </div>
              <p className="text-lg text-gray-800">{finalDecision}</p>
            </div>
          ) : (
            <p className="text-lg text-gray-800 text-center font-medium">
              {question}
            </p>
          )}
        </div>

        {!isFinished && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <button
              onClick={() => handleAnswer('Yes')}
              disabled={isLoading}
              className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Yes
            </button>
            <button
              onClick={() => handleAnswer('No')}
              disabled={isLoading}
              className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              No
            </button>
            <button
              onClick={() => handleAnswer('Maybe')}
              disabled={isLoading}
              className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Maybe
            </button>
          </div>
        )}

        {showHistory && history.length > 0 && (
          <div className="border-t pt-6">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-800">Previous Questions</h2>
            </div>
            <div className="space-y-3">
              {history.map((qa, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-700 text-sm">{qa.question}</p>
                  <p className="text-right mt-1">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      qa.answer === 'Yes' ? 'bg-green-100 text-green-800' :
                      qa.answer === 'No' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {qa.answer}
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;